import {
  Autocomplete,
  Box,
  Button,
  Fade,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { styles } from "./styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../componentes/notificationProvider";
import api from "../../services/api";
import NewAlertModal from "../../componentes/newAlertModal";
import {
  Add,
  Cached,
  Clear,
  Dns,
  ReadMore,
  Router,
  Search,
  Upload,
} from "@mui/icons-material";
import LoadingPage from "../../componentes/loadingPage";
import CadastroTable from "./cadastroTable";
import FormHostModal from "./modalFormHost";
import Papa from "papaparse";
import { v4 as uuidv4 } from "uuid";
import HostDescriptionModal from "./modalHostDescription";

export default function EasyAdd({ socket }) {
  const [hostsCadastrados, setHostsCadastrados] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [proxys, setProxys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  // filters
  const [buscarHost, setBuscarHost] = useState("");
  const [fabricanteFilter, setFabricanteFilter] = useState("");
  const [modeloFilter, setModeloFilter] = useState("");
  const [proxyFilter, setProxyFilter] = useState("");
  const [statusPingFilter, setStatusPingFilter] = useState("");
  const [statusSnmpFilter, setStatusSnmpFilter] = useState("");
  const [loadingProxys, setLoadingProxys] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [openCreateHostModal, setOpenCreateHostModal] = useState(false);
  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [confirmLoad, setConfirmLoad] = useState(false);
  const [hostToDelete, setHostToDelete] = useState(null);
  const [editingHostData, setEditingHostData] = useState(null);
  const [editingHostDescriptionModal, setEditingHostDescriptionModal] =
    useState(false);
  const [role, setRole] = useState("");

  const theme = useTheme();
  const s = styles(theme);
  const navigate = useNavigate();
  const notify = useNotification();
  const modalFormHostRef = useRef();
  const fileInputRef = useRef(null);

  const easyMapsOnly =
    window?.config?.REACT_APP_EASYMAPS_ONLY === "true" || false;

  useEffect(() => {
    setRole(
      JSON.parse(localStorage.getItem("1")).usuarioCliente.codigoPermissao,
    );
    fetchAllData();
  }, []);

  const fetchAllData = () => {
    apiGetHostsCadastrados();
    apiGetTemplates();
    apitGetProxys();
  };

  async function apiGetHostsCadastrados() {
    setLoading(true);
    try {
      const response = await api.get("/easyAdd/zabbix/hosts");
      if (response?.data) {
        setHostsCadastrados(response?.data);
      }
    } catch (error) {
      console.error(error);
      notify(
        error?.response?.data?.message || "Erro ao buscar hosts cadastrados",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }

  async function apiGetTemplates() {
    try {
      setLoadingTemplates(true);
      const response = await api.get("/predefinicoes/findMany");
      setTemplates(response?.data || []);
    } catch (error) {
      console.error(error);
      notify(
        error?.response?.data?.message || "Erro ao buscar predefinições",
        "error",
      );
    } finally {
      setLoadingTemplates(false);
    }
  }

  async function apitGetProxys(params) {
    try {
      setLoadingProxys(true);
      const reponse = await api.get("/easyAdd/zabbix/proxys", { params });
      setProxys(reponse?.data);
    } catch (error) {
      console.error(error);
      notify(
        `Error:${error?.response?.data?.error || "Ao buscar os proxys"} `,
        "error",
      );
    } finally {
      setLoadingProxys(false);
    }
  }
  async function createHost(host) {
    try {
      setLoadingCreate(true);
      const response = await api.post("easyAdd/zabbix/createHost", {
        hostData: host,
      });
      if (response.data.error) {
        const error = response.data.error;
        notify(
          `Erro ao criar host: ${error.code} - ${error.data}`,
          "error",
          5000,
        );
        return;
      }
      const hostid = response?.data?.data;
      host.id = hostid;
      let newHosts = [...hostsCadastrados];
      host.checks = {
        ping: host?.statusPing,
        snmp: host?.statusSnmp,
        ssh: null,
      };

      newHosts.push(host);
      setHostsCadastrados(newHosts);
      modalFormHostRef?.current?.close();

      const grafanaSummary = response?.data?.grafanaSummary;
      let finalMessage = "Zabbix: Host criado com sucesso.";
      let severity = "success";

      if (grafanaSummary) {
        const isError = grafanaSummary.toLowerCase().includes("erro");
        finalMessage += `\nGrafana: ${grafanaSummary}`;
        if (isError) severity = "warning";
      }

      notify(finalMessage, severity, 6000);
    } catch (error) {
      console.error(error);
      notify(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erro ao criar host.",
        "error",
      );
    } finally {
      setLoadingCreate(false);
    }
  }

  async function updateHost(host, hostFormatted) {
    try {
      setLoadingCreate(true);
      const response = await api.put("easyAdd/zabbix/updateHost", {
        hostData: host,
      });
      if (response.data.error) {
        const error = response.data.error;
        notify(
          `Erro ao atualizar host: ${error.code} - ${error.data}`,
          "error",
          5000,
        );
        return;
      }
      let newHosts = [...hostsCadastrados];
      const index = newHosts.findIndex((h) => h.id === host.hostId);

      if (index !== -1) {
        newHosts[index] = hostFormatted;
        newHosts[index].checks = {
          ping: host?.statusPing,
          snmp: host?.statusSnmp,
          ssh: null,
        };
      }
      setHostsCadastrados(newHosts);
      modalFormHostRef?.current?.close();

      notify("Host atualizado com sucesso.", "success");
    } catch (error) {
      console.error(error);
      notify(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erro ao atualizar host.",
        "error",
      );
    } finally {
      setLoadingCreate(false);
    }
  }

  async function updateHostDescription(host, hostFormatted) {
    try {
      setLoadingCreate(true);
      const response = await api.put("easyAdd/zabbix/updateHostDescription", {
        hostData: host,
      });
      if (response.data.error) {
        const error = response.data.error;
        notify(
          `Erro ao atualizar descrição do host: ${error.code} - ${error.data}`,
          "error",
          5000,
        );
        return;
      }
      let newHosts = [...hostsCadastrados];
      const index = newHosts.findIndex((h) => h.id === host.hostId);

      if (index !== -1) {
        newHosts[index] = hostFormatted;
        newHosts[index].descricao = host.description;
      }
      setHostsCadastrados(newHosts);

      notify("Descrição do Host atualizada com sucesso.", "success");
    } catch (error) {
      console.error(error);
      notify(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erro ao atualizar descrição do host.",
        "error",
      );
    } finally {
      setLoadingCreate(false);
      setEditingHostData(null);
      setEditingHostDescriptionModal(false);
    }
  }

  const handleRequestDelete = (hostId) => {
    setHostToDelete(hostId);
    setOpenModalConfirmacao(true);
  };

  async function deleteHost() {
    if (!hostToDelete) return;

    try {
      setOpenModalConfirmacao(false);
      setConfirmLoad(true);
      await api.delete("easyAdd/zabbix/deleteHost", {
        data: { id: hostToDelete },
      });

      let newHosts = [...hostsCadastrados];
      newHosts = newHosts.filter(
        (host) => (host.hostId || host.id) !== hostToDelete,
      );
      setHostsCadastrados(newHosts);

      notify("Host excluído com sucesso.", "success");
      setHostToDelete(null);
    } catch (error) {
      console.error(error);
      notify("Erro ao excluir host.", "error");
    } finally {
      setConfirmLoad(false);
    }
  }
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      encoding: "UTF-8",
      complete: (results) => {
        const data = results.data.map((row) => ({
          ...row,
          _errors: {},
          Status: {
            ping: null,
            snmp: null,
            ssh: null,
            telnet: null,
          },
          id: uuidv4(),
        }));
        navigate(easyMapsOnly ? "/easyMaps/hosts" : "/easyAdd/hosts", {
          state: { initialCsvData: data },
        });
        e.target.value = null;
      },
      error: (error) => {
        notify("Erro ao ler o arquivo CSV", "error");
      },
    });
  };

  const { fabricantes, modelos } = useMemo(() => {
    let map = [];
    const list = [];
    if (Array.isArray(templates)) {
      templates.forEach((item) => {
        const key = item?.fabricante;
        if (key) {
          list.push(key);
          map = [
            ...map,
            ...item?.Funcoes.flatMap((f) =>
              f.Modelos.map((m) => `${f.funcao} - ${m.modelo}`),
            ),
          ];
        }
      });
    }
    return { fabricantes: list, modelos: map };
  }, [templates]);

  return (
    <Box sx={s.container}>
      <Box sx={s.filterBox}>
        <TextField
          id={"host-search"}
          size="small"
          placeholder="Buscar..."
          value={buscarHost}
          onChange={(event) => setBuscarHost(event.target?.value)}
          sx={{
            ...s.textField,
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.textField,
            },
          }}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: buscarHost ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setBuscarHost("")}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack sx={s.filterStack}>
          <Box sx={s.autocompleteBox}>
            <Autocomplete
              options={proxys}
              sx={{ flex: 1 }}
              getOptionLabel={(option) => option?.host ?? ""}
              value={proxys.find((p) => p.host === proxyFilter) || null}
              onChange={(event, newValue) => {
                setProxyFilter(newValue ? newValue.host : "");
              }}
              isOptionEqualToValue={(option, value) =>
                option.host === value.host
              }
              loading={loadingProxys}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecione o proxy"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: theme.palette.background.textField,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <ReadMore
                            sx={{ color: theme.palette.text.disabled }}
                            fontSize="small"
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                  <Fade in={loadingProxys}>
                    <LinearProgress />
                  </Fade>
                </>
              )}
              size="small"
              noOptionsText={
                loadingProxys
                  ? "Carregando proxies..."
                  : "Nenhum proxy encontrado"
              }
            />
            <Autocomplete
              options={fabricantes}
              sx={{ flex: 1 }}
              getOptionLabel={(option) => option ?? ""}
              value={fabricantes.find((f) => f === fabricanteFilter) || null}
              onChange={(event, newValue) => {
                setFabricanteFilter(newValue ? newValue : "");
              }}
              isOptionEqualToValue={(option, value) => option === value}
              loading={loadingTemplates}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecione o fabricante"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: theme.palette.background.textField,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Dns
                            sx={{ color: theme.palette.text.disabled }}
                            fontSize="small"
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                  <Fade in={loadingTemplates}>
                    <LinearProgress />
                  </Fade>
                </>
              )}
              size="small"
              noOptionsText={
                loadingTemplates
                  ? "Carregando fabricantes..."
                  : "Nenhum fabricante encontrado"
              }
            />
            <Autocomplete
              options={modelos || []}
              sx={{ flex: 1 }}
              getOptionLabel={(option) => option ?? ""}
              value={modelos?.find((m) => m === modeloFilter) || null}
              onChange={(event, newValue) => {
                setModeloFilter(newValue ? newValue : "");
              }}
              isOptionEqualToValue={(option, value) => option === value}
              loading={loadingTemplates}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecione o modelo"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: theme.palette.background.textField,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Router
                            sx={{ color: theme.palette.text.disabled }}
                            fontSize="small"
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                  <Fade in={loadingTemplates}>
                    <LinearProgress />
                  </Fade>
                </>
              )}
              size="small"
              noOptionsText={
                loadingTemplates
                  ? "Carregando modelos..."
                  : "Nenhum modelo encontrado"
              }
            />
          </Box>
          <Box sx={s.actionsBox}>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button
              startIcon={<Upload />}
              variant="outlined"
              sx={s.buttonImport}
              onClick={() => fileInputRef.current.click()}
              disabled={loading}
            >
              Importar CSV
            </Button>
            <Button
              startIcon={<Add />}
              variant="contained"
              sx={s.buttonNew}
              onClick={() => setOpenCreateHostModal(true)}
              disabled={loading}
            >
              Novo
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={loading}
              onClick={() => fetchAllData()}
              sx={s.buttonRefresh()}
            >
              <Cached
                fontSize="small"
                sx={{ color: theme.palette.text.main }}
                className={loading ? "rotating" : ""}
              />
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* <Tabs
        textColor="inherit"
        onChange={(event, newValue) => {
          setPageValue(newValue);
        }}
        sx={s.tabs}
        value={pageValue}
      >
        <Tab
          label={
            <Box sx={s.boxTab}>
              Hosts Cadastrados
              <Chip
                label={loading ? 0 : hostsCadastrados?.length || 0}
                size="small"
                sx={stylesChip("success")}
              />
            </Box>
          }
          value={"cadastrados"}
          sx={{ p: 0 }}
        />
      </Tabs> */}

      {loading ? (
        <Box sx={s.boxLoading}>
          <LoadingPage mt={"5%"} />
        </Box>
      ) : (
        <CadastroTable
          hostsCadastrados={hostsCadastrados ?? []}
          templates={templates}
          setOpenEditHostModal={setOpenCreateHostModal}
          setEditingHostData={setEditingHostData}
          setEditingHostDescriptionModal={setEditingHostDescriptionModal}
          handleRequestDelete={handleRequestDelete}
          proxys={proxys}
          buscarHost={buscarHost}
          proxyFilter={proxyFilter}
          fabricanteFilter={fabricanteFilter}
          modeloFilter={modeloFilter}
          statusPingFilter={statusPingFilter || ""}
          statusSnmpFilter={statusSnmpFilter || ""}
          loadingTable={confirmLoad}
          loading={loading}
          role={role}
        />
      )}

      <FormHostModal
        ref={modalFormHostRef}
        open={openCreateHostModal}
        onClose={() => setOpenCreateHostModal(false)}
        onSave={editingHostData ? updateHost : createHost}
        templates={templates}
        loading={loadingCreate}
        proxys={proxys}
        hostData={editingHostData}
        setEditingHostData={setEditingHostData}
        role={role}
      />

      <HostDescriptionModal
        open={editingHostDescriptionModal}
        onClose={() => setEditingHostDescriptionModal(false)}
        onSave={updateHostDescription}
        hostData={editingHostData}
        setEditingHostData={setEditingHostData}
        loading={loadingCreate}
      />

      <NewAlertModal
        openModal={openModalConfirmacao}
        setOpenModal={setOpenModalConfirmacao}
        textContent={"Tem certeza que deseja excluir o host?"}
        handleButton={() => {
          deleteHost();
        }}
        severity={"error"}
        singleButton={false}
      />
    </Box>
  );
}
